import { AlertProps, Link } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  useCreateEcfrPinMutation,
  useSendPinResetEmailMutation,
  useValidateEcfrPinMutation,
} from "../../../../../../hooks/mutations";
import { User } from "../../../../../../stores/models";
import GenericModal from "../../../../../GenericModal/GenericModal";
import InsertPinInput from "./insert/InsertPinInput";
import CreatePinInput from "./setup/SetupPinInput";
import {
  handleConfirmPinChange,
  handlePinChange,
  hasPin,
  setupPinWarningText,
} from "./utils";

const documentApprovalAlert: {
  severity: AlertProps["severity"];
  message: string;
} = {
  severity: "warning",
  message:
    "Please review the document thoroughly before approving it. By pressing the button below, you are certifying that you have reviewed it and approve of its content.",
};

export const ApprovalPinModal = ({
  isOpen,
  onClose,
  onApprove,
  user,
  title,
  description,
  saveText,
  confirmationWarningText,
}: {
  isOpen: boolean;
  onClose: () => void;
  // The validated PIN is passed through to onApprove so callers that need to
  // forward it to the backend (e.g. training task signing) can do so. Most
  // callers (document approval) ignore the argument and rely on the modal's
  // own validateEcfrPinMutation call.
  onApprove: (pin?: string) => Promise<void>;
  user: User;
  // Optional copy overrides — defaults to document-approval wording.
  title?: string;
  description?: React.ReactNode;
  saveText?: string;
  confirmationWarningText?: string;
}) => {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [alert, setAlert] = useState<{
    severity: AlertProps["severity"];
    message: string;
  } | null>(null);
  const [activePinIndex, setActivePinIndex] = useState<number>(0);
  const [isPinFocused, setIsPinFocused] = useState<boolean>(false);
  const [isCreatePin, setIsCreatePin] = useState(!hasPin(user));
  const [isLoading, setIsLoading] = useState(false);
  const pinInputRef = useRef<HTMLInputElement>(null);
  const createEcfrPinMutation = useCreateEcfrPinMutation();
  const validateEcfrPinMutation = useValidateEcfrPinMutation();
  const sendResetEmailMutation = useSendPinResetEmailMutation();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPin("");
      setConfirmPin("");
      setAlert(null);
      setActivePinIndex(0);
      setIsPinFocused(false);
      setIsLoading(false);
    } else {
      const isSettingPin = !hasPin(user);

      setIsCreatePin(isSettingPin);
      setAlert(
        isSettingPin
          ? null
          : {
              ...documentApprovalAlert,
              message:
                confirmationWarningText ?? documentApprovalAlert.message,
            }
      );
      // Focus the PIN input when modal opens
      setTimeout(() => {
        if (pinInputRef.current) {
          pinInputRef.current.focus();
          pinInputRef.current.setSelectionRange(0, 0);
          setIsPinFocused(true);
        }
      }, 100);
    }
  }, [isOpen]);

  const resetInsertPinInput = () => {
    setPin("");
    setActivePinIndex(0);
    pinInputRef?.current?.focus();
  };

  useEffect(() => {
    if (alert && !isCreatePin) {
      resetInsertPinInput();
    }
  }, [alert, isCreatePin]);

  const handlePinSelect = () => {
    setTimeout(() => {
      const input = document.activeElement as HTMLInputElement;
      if (input && input.type === "password") {
        const cursorPosition = input.selectionStart || 0;
        const activeIndex = Math.min(cursorPosition, 3);
        setActivePinIndex(activeIndex);
      }
    }, 0);
  };

  const handleValidatePin = async (): Promise<boolean> => {
    const result = await validateEcfrPinMutation.mutateAsync(pin);
    if (!result.isValid) {
      setAlert({
        severity: "error",
        message: "Invalid PIN. Please try again.",
      });
    }

    return result.isValid;
  };

  const handleCreatePin = async () => {
    if (pin !== confirmPin) {
      setAlert({ severity: "error", message: "PINs do not match" });
    }

    const result = await createEcfrPinMutation.mutateAsync(pin);

    if (result.error) {
      setAlert({
        severity: "error",
        message: "Failed to create PIN. Try again.",
      });

      setPin("");
      setConfirmPin("");
      setIsLoading(false);

      return;
    }

    await onApprove(pin);
    onClose();

    return;
  };

  const handleInsertPin = async () => {
    const isValid = await handleValidatePin();

    if (!isValid) {
      setIsLoading(false);
      resetInsertPinInput();

      setAlert({
        severity: "error",
        message: "Failed to validate PIN. Please try again.",
      });

      return;
    }

    await onApprove(pin);
    onClose();
  };

  const handleApprove = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsLoading(true);

    isCreatePin ? handleCreatePin() : handleInsertPin();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isDisabled) {
      e.preventDefault();
      handleApprove();
    }
  };

  const isPinInvalid =
    pin.length !== 4 || confirmPin.length !== 4 || pin !== confirmPin;

  const isDisabled =
    isLoading || (isCreatePin ? isPinInvalid : pin.length !== 4);

  const alertContent = alert?.message;

  const handleFocus = () => {
    setIsPinFocused(true);
    handlePinSelect();
  };

  const handleBlur = () => {
    setIsPinFocused(false);
  };

  const onPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePinChange(e, setPin, confirmPin, setAlert);
  };

  const onConfirmPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleConfirmPinChange(e, setConfirmPin, pin, setAlert);
  };

  const handleSendResetEmail = async (e: React.MouseEvent) => {
    e.preventDefault();
    await sendResetEmailMutation.mutateAsync();
    setAlert({
      severity: "success",
      message: "A PIN reset link has been sent to your email address.",
    });
  };

  const enterPinDescription = (
    <>
      {description ?? "Enter your 4-digit PIN to approve this document."}{" "}
      <Link href="#" onClick={handleSendResetEmail} sx={{ cursor: "pointer" }}>
        Request a pin reset here
      </Link>
      .
    </>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      title={title ?? (isCreatePin ? "Set Approval Pin" : "Enter Approval Pin")}
      description={
        isCreatePin
          ? description ??
            "Please set a pin that will be used for all approvals within Formly."
          : enterPinDescription
      }
      handleSave={handleApprove}
      handleCancel={onClose}
      isDisabled={isDisabled}
      isLoading={isLoading}
      saveText={saveText ?? "Confirm"}
      width="550px"
      {...(isCreatePin && {
        alertText: setupPinWarningText,
        alertSeverity: "warning",
      })}
      disableRestoreFocus={true}
    >
      <div className="flex flex-col gap-3">
        {isCreatePin ? (
          <CreatePinInput
            pin={pin}
            confirmPin={confirmPin}
            onPinChange={onPinChange}
            onConfirmPinChange={onConfirmPinChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            alert={{ severity: alert?.severity, alertContent }}
          />
        ) : (
          <InsertPinInput
            pin={pin}
            onPinChange={onPinChange}
            onPinSelect={handlePinSelect}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={alert?.message || null}
            activePinIndex={activePinIndex}
            isPinFocused={isPinFocused}
            pinInputRef={pinInputRef}
            onKeyDown={handleKeyDown}
            alert={{ severity: alert?.severity, alertContent }}
          />
        )}
      </div>
    </GenericModal>
  );
};

export default ApprovalPinModal;
